<script setup lang="ts">
import { Ref, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import InputText from 'primevue/inputtext'
import { updateFolder } from './api'
import { useLibraryStore } from './library.store'
import { useUserStore } from './../authentication/user.store'
import StudentPIITooltip from '@/components/StudentPIITooltip.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'

const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  }, 
  reloadFolders: {
    type: Boolean,
    default: false, 
    required: false
  }
})
const emit = defineEmits(['update:externalOpen', 'folderEdited'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const analytics = getAnalyticsTracker()
const { folders, currentFolder } = storeToRefs(libraryStore)
const existingFolderNames = computed(() => {
  return folders.value.map((item: { name: string }) => {
    return item.name.toLowerCase()
  })
})

const originalFolderName = computed(() => {
  return currentFolder.value.name
})
const newFolderName = ref(originalFolderName.value)
watch(originalFolderName, () => {
  newFolderName.value = originalFolderName.value
})

const isNewName = computed(() => {
  if (newFolderName.value) {
    return !existingFolderNames.value
      .filter((s) => s.toLowerCase() !== originalFolderName.value.toLowerCase())
      .includes(newFolderName.value.toLowerCase())
  } else {
    return false
  }
})

const isNameChanged = computed(() => {
  return !(newFolderName.value.toLowerCase() === originalFolderName.value.toLowerCase())
})

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const createError: Ref<boolean> = ref(false)

const closeModal = function (): void {
  visibleRef.value = false
  newFolderName.value = originalFolderName.value
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
    if (props.externalOpen && props.reloadFolders) {
      libraryStore.fetchFolders(userStore.userId)
    }
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const disableSubmit = computed(() => {
  return newFolderName.value === '' || isNameChanged.value === false || !isNewName.value
})

const sendUpdateFolder = async function () {
  if (!disableSubmit.value) {
    try {
      await updateFolder({
        userUuid: userStore.userId,
        folderUuid: currentFolder.value.uuid,
        updateParams: {
          name: newFolderName.value
        }
      })
      const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()
      analytics.sendEvent('Edited Student', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent('Edited Folder', getEventData.value)
      libraryStore.fetchFolders(userStore.userId)
      emit('folderEdited')
      closeModal()
      createError.value = false
    } catch (e) {
      createError.value = true
      console.error(e)
    }
  }
}
</script>
<template>
  <GbDialog
    dialogId="rename-folder-modal"
    v-model:visible="visibleRef"
    header="Rename Folder"
    :footer-buttons="true"
    @hide="closeModal"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2">
        <label for="foldername">
          Folder Name
          <span class="absolute ml-2"><StudentPIITooltip /></span>
        </label>
        <InputText id="foldername" v-model="newFolderName" autofocus @keyup.enter="sendUpdateFolder" class="fs-exclude"/>
        <GbInlineMessage gbTheme="red" v-if="!isNewName && newFolderName"
          >You may not have two folders with the same name.</GbInlineMessage
        >
        <GbInlineMessage gbTheme="red" v-if="createError"
          >We couldn't update the folder. Try again later.</GbInlineMessage
        >
      </div>
    </template>
    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      ></GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Save"
        @click="sendUpdateFolder"
        :disabled="disableSubmit"
        :aria-disabled="disableSubmit"
      ></GbButton>
    </template>
  </GbDialog>
</template>
